import { useSeoMeta } from '@unhead/react';
import { useEffect, useRef, useState } from 'react';
import { 
  Terminal, 
  Shield, 
  GitBranch, 
  Bot, 
  Layers, 
  Radio, 
  ArrowRight,
  ExternalLink,
  ChevronDown,
  Zap,
  Lock,
  Code2,
  Cpu,
  Eye,
  Clock,
  Flag,
  Wrench,
  Globe,
  HardDrive,
  Mail,
  Rocket,
  RadioTower,
  Workflow,
  Menu,
  X,
  Sparkles,
  Key,
  MessageCircle,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteFooter } from '@/components/SiteFooter';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */
const GithubIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Nori SVG Mascot                                                    */
/* ------------------------------------------------------------------ */
const NoriLogo = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg 
    viewBox="0 0 20 16" 
    shapeRendering="crispEdges" 
    aria-label="Nori the ostrich" 
    className={className}
  >
    <g fill="#7B68EE">
      <rect x="8" y="0" width="2" height="1"/>
      <rect x="7" y="1" width="1" height="2"/>
      <rect x="10" y="1" width="1" height="2"/>
      <rect x="6" y="3" width="2" height="1"/>
      <rect x="10" y="3" width="2" height="1"/>
      <rect x="4" y="4" width="10" height="1"/>
      <rect x="3" y="5" width="1" height="3"/>
      <rect x="14" y="5" width="1" height="3"/>
      <rect x="7" y="6" width="2" height="1"/>
      <rect x="4" y="8" width="2" height="1"/>
      <rect x="12" y="8" width="2" height="1"/>
      <rect x="6" y="9" width="6" height="1"/>
      <rect x="6" y="10" width="1" height="1"/>
      <rect x="11" y="10" width="1" height="1"/>
      <rect x="5" y="11" width="2" height="1"/>
      <rect x="11" y="11" width="2" height="1"/>
      <rect x="5" y="12" width="1" height="2"/>
      <rect x="12" y="12" width="1" height="2"/>
      <rect x="4" y="14" width="3" height="1"/>
      <rect x="9" y="14" width="4" height="1"/>
      <rect x="3" y="15" width="1" height="1"/>
      <rect x="6" y="15" width="1" height="1"/>
      <rect x="9" y="15" width="1" height="1"/>
      <rect x="12" y="15" width="1" height="1"/>
    </g>
    <g fill="#C4BBFF">
      <rect x="8" y="1" width="2" height="2"/>
      <rect x="8" y="3" width="2" height="1"/>
      <rect x="4" y="5" width="10" height="1"/>
      <rect x="4" y="6" width="3" height="1"/>
      <rect x="9" y="6" width="5" height="1"/>
      <rect x="4" y="7" width="10" height="1"/>
      <rect x="6" y="8" width="6" height="1"/>
      <rect x="7" y="10" width="4" height="1"/>
      <rect x="7" y="11" width="4" height="1"/>
      <rect x="6" y="12" width="6" height="1"/>
      <rect x="6" y="13" width="1" height="1"/>
      <rect x="9" y="13" width="3" height="1"/>
    </g>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Navigation                                                         */
/* ------------------------------------------------------------------ */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '/' },
    { label: 'Stack', href: '/stack' },
    { label: 'Get Started', href: '/get-started' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background/80 backdrop-blur-xl border-b border-border/50'
          : 'bg-transparent'
      }`}
    >
      <div className="container grid grid-cols-[1fr_1fr] lg:grid-cols-[1fr_auto_1fr] items-center h-16">
        <div className="flex items-center">
          <a href="/" className="flex items-center gap-2.5 group shrink-0">
            <NoriLogo className="w-7 h-7 transition-transform group-hover:scale-110 shrink-0" />
            <span className="font-semibold text-lg tracking-tight whitespace-nowrap">
              nostr-station
            </span>
          </a>
        </div>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`text-sm transition-colors whitespace-nowrap ${
                link.href === '/'
                  ? 'text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-3">
          <a
            href="https://github.com/jared-logan/nostr-station"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex"
          >
            <button className="inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors">
              <GithubIcon className="w-5 h-5" />
            </button>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden inline-flex items-center justify-center rounded-full h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border/50">
          <nav className="container py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className={`text-sm transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 ${
                  link.href === '/'
                    ? 'text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://github.com/jared-logan/nostr-station"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-accent/50 flex items-center gap-2"
            >
              <GithubIcon className="w-4 h-4" />
              GitHub
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ------------------------------------------------------------------ */
/*  Hero Section                                                       */
/* ------------------------------------------------------------------ */
const HeroSection = () => {
  const [copied, setCopied] = useState(false);

  const copyCommand = () => {
    navigator.clipboard.writeText('curl -fsSL https://raw.githubusercontent.com/jared-logan/nostr-station/main/install.sh | bash');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(265_85%_60%/0.15),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_50%_50%,hsl(265_70%_55%/0.08),transparent)]" />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(hsl(265 85% 60%) 1px, transparent 1px), linear-gradient(90deg, hsl(265 85% 60%) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/20 bg-primary/5 text-sm text-primary mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
            </span>
            Your personal Nostr, self-hosted
          </div>

          {/* Title */}
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6">
            <span className="text-gradient">nostr-station</span>
          </h1>

          {/* Description */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            Run your own relay, client, media, mail, and AI — the whole Nostr stack on hardware you own, in one command.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a 
              href="https://github.com/jared-logan/nostr-station" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 text-base">
                <GithubIcon className="w-5 h-5 mr-2" />
                View on GitHub
              </Button>
            </a>
            <a href="#features">
              <Button size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-glow">
                Explore Features
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          {/* Command Line */}
          <div id="install" className="relative max-w-2xl mx-auto mb-3">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 rounded-xl blur-lg" />
            <div className="relative rounded-xl bg-[#0d0d12] border border-primary/20 p-4 sm:p-6 text-left">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-primary" />
                  <span className="text-xs text-muted-foreground">Terminal</span>
                </div>
                <button 
                  onClick={copyCommand}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                >
                  {copied ? 'Copied!' : 'Copy'}
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              <code className="block text-sm sm:text-base font-mono text-foreground/90 break-all">
                <span className="text-primary">$</span> curl -fsSL https://raw.githubusercontent.com/jared-logan/nostr-station/main/install.sh | bash
              </code>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-2">
            Get your complete Nostr stack running in minutes. No accounts, no cloud, no complex config.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            Installs in ~30 seconds. No Docker, no Rust toolchain, no sudo.
          </p>

          {/* Tags */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-muted-foreground mb-12">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              <span>Local-first</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <span>Self-hosted</span>
            </div>
            <div className="flex items-center gap-2">
              <Code2 className="w-4 h-4 text-primary" />
              <span>Open source</span>
            </div>
          </div>

          {/* Large Nori */}
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full" />
            <NoriLogo className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto drop-shadow-2xl" />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <a 
        href="#about" 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground hover:text-foreground transition-colors animate-bounce"
      >
        <ChevronDown className="w-6 h-6" />
      </a>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  About Section                                                      */
/* ------------------------------------------------------------------ */
const AboutSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="container">
        <div className={`max-w-3xl mx-auto text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            01 — About
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Your self-hosted Nostr
            <span className="text-gradient"> stack</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            nostr-station is your personal Nostr infrastructure: a self-contained hub that 
            puts you in full control of your data, communication, and creative work. Run your 
            own relay and media server, host a private client, send encrypted mail over Nostr, 
            reach your machines over a mesh VPN, and plug AI assistants into your workflow — 
            all on hardware you own. Whether you're browsing, building, hosting a community, 
            or just owning your corner of the network, it's configured and ready.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Features Section                                                   */
/* ------------------------------------------------------------------ */
const useNostrFeatures = [
  {
    icon: Globe,
    title: 'Self-hosted Client',
    description: 'Your own Ditto instance running locally. Browse, post, and interact with Nostr through a web client you fully control.',
    color: 'hsl(285 85% 60%)',
  },
  {
    icon: Mail,
    title: 'nostr-mail',
    description: 'Encrypted email over Nostr. NIP-17 gift-wrap hides sender and recipient metadata, RFC-2822 wire format inside sealed DMs, Blossom attachments, no SMTP — mail that lives on relays.',
    color: 'hsl(295 85% 60%)',
    href: '/nostr-mail',
  },
  {
    icon: Eye,
    title: 'nsite Browser',
    description: 'Browse and preview Nostr-hosted static sites locally before you publish. Your nsite, exactly as visitors will see it.',
    color: 'hsl(305 85% 60%)',
    href: '/nsite',
  },
  {
    icon: Shield,
    title: 'Mesh VPN',
    description: 'A private mesh VPN for secure access to your machines from anywhere.',
    color: 'hsl(280 85% 60%)',
    href: '/nvpn',
  },
];

const buildOnNostrFeatures = [
  {
    icon: Radio,
    title: 'Local Relay',
    description: 'A built-in Nostr relay on your machine for fast, private development and testing.',
    color: 'hsl(265 85% 60%)',
  },
  {
    icon: HardDrive,
    title: 'Local Blossom Server',
    description: 'A lightweight Blossom media server alongside your relay. Store and serve blobs locally — no external uploads.',
    color: 'hsl(275 85% 60%)',
  },
  {
    icon: GitBranch,
    title: 'ngit',
    description: 'Native git over Nostr with Amber signing. Version control your projects under your cryptographic identity.',
    color: 'hsl(250 85% 60%)',
    href: '/ngit',
  },
  {
    icon: Bot,
    title: 'AI Providers',
    description: 'Bring your own AI agents and models. Claude Code, OpenCode, and other terminal agents wired directly into your workflow. Local models (Ollama, LM Studio) keep prompts on your hardware.',
    color: 'hsl(290 85% 60%)',
  },
  {
    icon: Layers,
    title: 'Stacks Integration',
    description: 'Scaffold Bitcoin-secured Nostr apps from one terminal. `stacks mkstack` to bootstrap, `stacks agent` for AI-assisted builds, Clarity smart-contract layer alongside your Nostr services.',
    color: 'hsl(260 85% 60%)',
  },
  {
    icon: MessageCircle,
    title: 'Client',
    description: 'A real Nostr social client built into your station. Embedded Ditto — read your feed, post notes, see mentions, manage your profile. Signed by Amber, relays of your choice, no second app to install.',
    color: 'hsl(255 85% 60%)',
    href: '/client',
  },
  {
    icon: Sparkles,
    title: 'Nori',
    description: 'Your AI dev partner, built into the dashboard. Chat-driven coding with full project context. Multi-provider (Claude, OpenAI, Ollama, LM Studio, Groq, OpenRouter). API keys live in your OS keychain, never in config files.',
    color: 'hsl(270 85% 60%)',
    href: '/nori',
  },
];

const FeatureCard = ({ feature, index, visible }: { feature: typeof useNostrFeatures[0]; index: number; visible: boolean }) => {
  const cardContent = (
    <>
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${feature.color}15` }}
      >
        <feature.icon 
          className="w-6 h-6" 
          style={{ color: feature.color }}
        />
      </div>
      <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {feature.description}
      </p>
      {feature.href && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary font-medium group-hover:gap-2.5 transition-all">
          Learn more
          <ArrowRight className="w-4 h-4" />
        </div>
      )}
    </>
  );

  const className = `group relative p-6 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  if (feature.href) {
    return (
      <a 
        key={feature.title}
        href={feature.href}
        className={`${className} block cursor-pointer`}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        {cardContent}
      </a>
    );
  }

  return (
    <div 
      key={feature.title}
      className={className}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {cardContent}
    </div>
  );
};

const FeaturesSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-24 sm:py-32 relative">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      
      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            02 — Features
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Everything Nostr, running on
            <span className="text-gradient"> your machine</span>
          </h2>
        </div>

        {/* Use Nostr group */}
        <div className="mb-12">
          <span className="text-primary text-xs font-medium tracking-wider uppercase mb-4 block">
            Use Nostr
          </span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {useNostrFeatures.map((feature, index) => (
              <FeatureCard key={feature.title} feature={feature} index={index} visible={visible} />
            ))}
          </div>
        </div>

        {/* Build on Nostr group */}
        <div>
          <span className="text-primary text-xs font-medium tracking-wider uppercase mb-4 block">
            Build on Nostr
          </span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {buildOnNostrFeatures.map((feature, index) => (
              <FeatureCard key={feature.title} feature={feature} index={index} visible={visible} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Dashboard Preview Section                                          */
/* ------------------------------------------------------------------ */
const DashboardPreview = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="dashboard" ref={sectionRef} className="py-24 sm:py-32 relative overflow-hidden">
      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            03 — Dashboard
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Monitor everything from
            <span className="text-gradient"> one place</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            A real-time view of your identity, projects, relay and Blossom status,
            client health, nostr-mail, and AI configuration — everything at a glance.
          </p>
        </div>

        <div className={`max-w-5xl mx-auto transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <a
            href="https://i.nostr.build/cZHIgynIL3XAtrqZ.png"
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl overflow-hidden border border-border/50 bg-[#0d0d12] shadow-2xl hover:border-primary/30 transition-colors"
          >
            <img
              src="https://i.nostr.build/cZHIgynIL3XAtrqZ.png"
              alt="nostr-station dashboard showing identity, projects, relay status, Blossom server, Ditto client, nostr-mail, service health, and installed binaries"
              className="w-full h-auto"
              loading="lazy"
            />
          </a>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/*  Why Section                                                        */
/* ------------------------------------------------------------------ */
const WhySection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const pillars = [
    {
      icon: Eye,
      title: 'The Vision',
      body: "Nostr should be something you own, not something you log into. nostr-station is your private corner of the network — a workshop for building, a home for browsing, mailing, and connecting — all on your hardware. Public when you want to be, private by default.",
      color: 'hsl(265 85% 60%)',
    },
    {
      icon: Clock,
      title: 'Why Now',
      body: "The Nostr protocol layer is solid. What's been missing is the workbench: a coherent local environment where every piece — relay, media, client, identity, mail — is already wired together. nostr-station is that workbench.",
      color: 'hsl(280 85% 60%)',
    },
  ];

  return (
    <section id="why" ref={sectionRef} className="py-24 sm:py-32 relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,hsl(265_85%_60%/0.06),transparent)]" />

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-primary text-sm font-medium tracking-wider uppercase mb-4 block">
            04 — Why nostr-station
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Built for the
            <span className="text-gradient"> full journey</span>
          </h2>
        </div>

        <div className="grid gap-6 mb-12 max-w-3xl mx-auto">
          {pillars.map((pillar, index) => (
            <div
              key={pillar.title}
              className={`group relative p-6 sm:p-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 hover:glow-purple ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start gap-5">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${pillar.color}15` }}
                >
                  <pillar.icon className="w-6 h-6" style={{ color: pillar.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold mb-2">{pillar.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {pillar.body}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Shipping Today / Coming Soon */}
        <div className={`max-w-3xl mx-auto space-y-6 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Shipping Today */}
          <div className="relative rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 sm:p-8">
            <div className="absolute -top-3 left-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-emerald-950 text-xs font-medium">
                <Zap className="w-3.5 h-3.5" />
                Shipping Today
              </span>
            </div>
            <div className="grid gap-4 pt-2">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Embedded Ditto client</strong> at /#client — feed, posting, profile, notifications, contacts.
                </p>
              </div>
            </div>
          </div>

          {/* Coming Soon */}
          <div className="relative rounded-2xl border border-dashed border-primary/30 bg-primary/5 p-6 sm:p-8">
            <div className="absolute -top-3 left-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                <Wrench className="w-3.5 h-3.5" />
                Coming Soon
              </span>
            </div>
            <div className="grid gap-4 pt-2">
              <div className="flex items-start gap-3">
                <Rocket className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Deployment options.</strong> Ship to nsite for instant Nostr hosting, or package for Titan browser distribution with offline capability.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <RadioTower className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Meshtastic to Nostr.</strong> Bridge mesh radio networks to Nostr relays for off-grid communication that syncs when online.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};



/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
const Index = () => {
  useSeoMeta({
    title: 'nostr-station — Your personal Nostr, self-hosted',
    description: 'Run your own relay, client, media, mail, and AI — the whole Nostr stack on hardware you own, in one command.',
    ogTitle: 'nostr-station',
    ogDescription: 'Run your own relay, client, media, mail, and AI — the whole Nostr stack on hardware you own, in one command.',
    twitterCard: 'summary_large_image',
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <FeaturesSection />

      {/* Amber Security Callout */}
      <section className="py-8 relative">
        <div className="container max-w-3xl mx-auto">
          <div className="rounded-xl border border-border/50 bg-accent/30 backdrop-blur-sm p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your nsec never touches this machine. Every signing operation — git pushes, blob uploads, profile updates — requires explicit approval on your phone via Amber (NIP-46).
            </p>
          </div>
        </div>
      </section>

      <DashboardPreview />
      <WhySection />
      <SiteFooter />
    </div>
  );
};

export default Index;
